import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { ColumnTypes } from "src/app/shared/Models/enums/ColumnTypeEnum";
import { FilterableTableComponent } from "src/app/shared/table/filterable-table.component";
import { ToastService } from "src/app/shared/toast.service";
import { DeviceCheckService } from "../../survey/shared/services/device-check.service";
import { Table } from "primeng/table";
import { first } from "rxjs/operators";
import { NavAction } from "src/app/shared/top-nav/top-nav.component";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { ActivatedRoute, Router } from "@angular/router";
import { SaveStateTypeEnum } from "src/app/shared/Models/enums/SaveStateTypeEnum";
import { BulkConfigurationType } from "../models/BulkConfigurationType.enum";
import { BulkService } from "../services/bulk.service";
import { BulkBlobDto } from "../models/BulkBlobDto";
import { BlobContainerType } from "../models/BlobContainerType.enum";
import { AdminPanelBulkUploadDialogComponent } from "./admin-panel-bulk-upload-dialog/admin-panel-bulk-upload-dialog.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: "app-admin-panel-bulk",
  templateUrl: "./admin-panel-bulk.component.html",
  styleUrls: ["./admin-panel-bulk.component.scss"],
})
export class AdminPanelBulkComponent
  extends FilterableTableComponent<BulkBlobDto>
  implements OnInit, AfterViewInit
{
  @ViewChild("dt") dataTable: Table;

  bulkConfigurationType: BulkConfigurationType;
  bulkHeaderTitle: string;

  navActions: NavAction[] = [];
  saveStates = SaveStateTypeEnum;

  scrollOffsetLandscape: number = 142;
  scrollOffsetPortrait: number = 142;

  allCols = [
    {
      field: "originalFileName",
      header: "ORIG FILE",
      visibleOnPortrait: true,
      hasFilter: false,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: false,
      hasEmptyValue: false,
    },
    {
      field: "bulkConfigurationTypeName",
      header: "CONFIG TYPE",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "creationDate",
      header: "CREATION DATE",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.DATE,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "isProcessed",
      header: "IS PROCESSED",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.BOOLEAN,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "numberOfLines",
      header: "NR LINES",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "numberOfSkippedLines",
      header: "NR SKIPPED LINES",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "numberOfSuccessfulLines",
      header: "NR SUCCESSFUL LINES",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "numberOfErrors",
      header: "NR ERRORS",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "requestor",
      header: "REQUESTOR",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
  ];

  constructor(
    private _router: Router,
    private route: ActivatedRoute,
    private _toastService: ToastService,
    private _bulkService: BulkService,
    private _deviceCheckService: DeviceCheckService,
    private _permissionsService: PermissionsService,
    private readonly modalService: NgbModal
  ) {
    super(_toastService, _deviceCheckService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.navActions = this.initNavActions();
  }

  private initNavActions(): NavAction[] {
    let list: NavAction[] = [];
    list.unshift(
      {
        icon: "exit_to_app",
        onClick: () => this.onGoBackButtonClick(),
        tooltip: "Return to Admin Page",
      },
      {
        icon: "upload_file",
        onClick: () => this.onUploadClick(),
        tooltip: "Upload bulk file",
      },
      {
        icon: "refresh",
        onClick: () => this.onRefreshClick(),
        tooltip: "Refresh table",
      }
    );

    return list;
  }

  ngAfterViewInit() {
    this.getAllBulkBlobs();
  }

  onDownloadOrigFile(fileName: string, blobContainerType: BlobContainerType) {
    // If the containertype is To Process, the original file is only available in the To Process container
    if (blobContainerType == BlobContainerType.BulkToProcess)
      this.onDownloadFile(fileName, BlobContainerType.BulkToProcess);
    else this.onDownloadFile(fileName, BlobContainerType.BulkProcessed);
  }

  onDownloadFile(fileName: string, blobContainerType: BlobContainerType) {
    // INFO: https://javascript.info/popup-windows
    this._bulkService.getDownloadLink(blobContainerType, fileName).subscribe(
      (res) => {
        setTimeout(() => window.open(res, "_blank"), 1000);
      },
      () => this.showErrorWhileDownloadingBulkBlob()
    );
  }

  onRefreshClick() {
    this.getAllBulkBlobs();
  }

  onUploadClick() {
    const modalRef = this.modalService.open(
      AdminPanelBulkUploadDialogComponent
    );
    modalRef.result.then((result) => {
      if (result === "ok") this.getAllBulkBlobs();
    });
  }

  public getBulkConfigurationTypeName(typeId) {
    return BulkConfigurationType[typeId];
  }

  private async onGoBackButtonClick() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`No internet`,
        $localize`Returning to Bulk.`
      );
      return;
    }

    this._router.navigate(["/admin"]);
  }

  private getAllBulkBlobs() {
    this._bulkService
      .getAllBulkBlobs()
      .pipe(first())
      .subscribe(
        (res) => {
          res.forEach((x) => {
            x.bulkConfigurationTypeName = this.getBulkConfigurationTypeName(
              x.bulkConfigurationType
            );
          });
          this.tableValues = res;
          this.globalFilter$.next(this.globalFilter);
        },
        () => this.showErrorWhileGettingBulkBlobs()
      );
  }

  private showErrorWhileGettingBulkBlobs() {
    this.toastService.showErrorToast(
      $localize`Something went wrong`,
      $localize`Something went wrong while retrieving the bulk blobs.`
    );
  }

  private showErrorWhileDownloadingBulkBlob() {
    this.toastService.showErrorToast(
      $localize`Something went wrong`,
      $localize`Something went wrong while downloading the bulk blob.`
    );
  }
}
